import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Grip } from 'lucide-react';
import { getAvatarColor } from '@/lib/avatar';
import { type EffetaApp } from '@/lib/schemas/common.schema';
import { cn } from '@/lib/utils';
import { useSessionStore } from '@/stores/session';

// Inicio de Effetá Apps: la suite de la que el PIM es una app.
export const EFFETA_APPS_URL = 'https://effeta.online';

const ITEM_CLASS =
  'data-[highlighted]:bg-bar-hover data-[highlighted]:outline-bar-fg flex items-center gap-3 rounded-md p-2 text-sm data-[highlighted]:outline-2 data-[highlighted]:-outline-offset-2 data-[highlighted]:outline-solid';

interface EffetaAppsMenuProps {
  isPhone: boolean;
}

// El menú de apps de la suite, igual en Effetá Apps y en cada app: el botón
// de 3x3 puntos justo a la derecha del logo, "Tus apps" para lectores de
// pantalla. Abre las apps de la persona en el orden del mosaico, cada una en
// una pestaña nueva; el PIM va marcado con "Estás aquí" y no se abre otra
// vez; al final, el enlace de texto "Ir a Effetá Apps" (línea gráfica de la
// suite, sección 6).
//
// La lista llega con cada `POST /auth/refresh` (`apps`). Sin apps (`[]`) el
// botón no se muestra. Si nunca llegó (sesión de antes del menú), queda solo
// el enlace a Effetá Apps, para no perder el camino de vuelta.
export const EffetaAppsMenu = ({ isPhone }: EffetaAppsMenuProps) => {
  const apps = useSessionStore((s) => s.apps);
  if (apps !== null && apps.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label="Tus apps"
        className={cn(
          'text-bar-fg hover:bg-bar-hover focus-visible:outline-bar-fg data-[state=open]:bg-bar-hover inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors',
          isPhone ? 'h-11 w-11' : 'h-9 w-9',
        )}
      >
        <Grip aria-hidden="true" className="h-5 w-5" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={10}
          className="bg-bar text-bar-fg z-50 flex max-h-(--radix-dropdown-menu-content-available-height) min-w-64 flex-col gap-1 overflow-y-auto rounded-md p-2 shadow-lg"
        >
          {(apps ?? []).map((app) =>
            app.current ? (
              // La app en la que ya se está: marcada, sin abrirla otra vez.
              <DropdownMenu.Item
                key={app.id}
                aria-current="page"
                onSelect={(event) => event.preventDefault()}
                className={cn(ITEM_CLASS, 'cursor-default')}
              >
                <AppIcon app={app} />
                <span className="flex flex-col">
                  {app.name}
                  <span className="text-xs opacity-80">Estás aquí</span>
                </span>
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Item key={app.id} asChild>
                <a
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(ITEM_CLASS, 'cursor-pointer')}
                >
                  <AppIcon app={app} />
                  {app.name}
                </a>
              </DropdownMenu.Item>
            ),
          )}
          {apps !== null && <DropdownMenu.Separator className="bg-bar-fg/30 mt-1 h-px" />}
          {/* Un enlace de texto, no otra app: el camino de vuelta a la suite. */}
          <DropdownMenu.Item asChild>
            <a
              href={EFFETA_APPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(ITEM_CLASS, 'cursor-pointer px-1.5 py-1.5 text-xs')}
            >
              Ir a Effetá Apps
            </a>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

// Igual que Effetá Apps (`AppImage`): "Effetá Creative" es "EC" y el color
// sale del nombre (`getAvatarColor`), así una app se ve igual en el mosaico
// y en cada menú.
const appInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter((word) => word !== '')
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

// Ícono de la app, o sus iniciales si no tiene o no carga (los sirve Effetá
// con la sesión de la persona: puede fallar).
const AppIcon = ({ app }: { app: EffetaApp }) => {
  const [hasFailed, setHasFailed] = useState(false);
  const showImage = app.iconUrl !== null && !hasFailed;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md text-xs font-semibold',
        showImage ? 'bg-bg p-1' : cn(getAvatarColor(app.name), 'text-avatar-fg'),
      )}
    >
      {showImage ? (
        <img
          src={app.iconUrl ?? undefined}
          alt=""
          referrerPolicy="no-referrer"
          className="h-full w-full object-contain"
          onError={() => setHasFailed(true)}
        />
      ) : (
        appInitials(app.name)
      )}
    </span>
  );
};
